(function () {
  'use strict';

  if (window.UnifiedBus) {
    return;
  }

  const VERSION = '1.0.0';
  const LEGACY_EVENT_NAME = 'unified:debug';
  const MAX_HISTORY = 200;

  const subscribers = new Map();
  const history = [];
  let sequence = 0;

  function normalizeType(type) {
    return String(type || '').trim().toLowerCase();
  }

  function cloneData(data) {
    if (!data || typeof data !== 'object') {
      return data || {};
    }

    try {
      return JSON.parse(JSON.stringify(data));
    } catch (error) {
      return {};
    }
  }

  function createEnvelope(type, data) {
    sequence += 1;

    return {
      id: sequence,
      type: normalizeType(type),
      timestamp: new Date().toISOString(),
      data: cloneData(data)
    };
  }

  function saveToHistory(envelope) {
    history.push(envelope);

    if (history.length > MAX_HISTORY) {
      history.shift();
    }
  }

  function getSubscribers(type) {
    const normalizedType = normalizeType(type);

    if (!subscribers.has(normalizedType)) {
      subscribers.set(normalizedType, new Set());
    }

    return subscribers.get(normalizedType);
  }

  function callSubscribers(type, envelope) {
    const directSubscribers = subscribers.get(type);
    const wildcardSubscribers = subscribers.get('*');
    const handlers = new Set();

    if (directSubscribers) {
      directSubscribers.forEach(function (handler) { handlers.add(handler); });
    }

    if (wildcardSubscribers) {
      wildcardSubscribers.forEach(function (handler) { handlers.add(handler); });
    }

    handlers.forEach(function (handler) {
      try {
        handler(envelope);
      } catch (error) {
        console.error('[Unified Bus] Subscriber failed', {
          type: type,
          error: error.message
        });
      }
    });
  }

  function emitLegacyEvent(envelope) {
    try {
      window.dispatchEvent(
        new CustomEvent(LEGACY_EVENT_NAME, { detail: envelope })
      );
      return true;
    } catch (error) {
      console.error('[Unified Bus] Legacy event dispatch failed', {
        error: error.message
      });
      return false;
    }
  }

  function emit(type, data) {
    const normalizedType = normalizeType(type);

    if (!normalizedType) {
      return null;
    }

    const envelope = createEnvelope(normalizedType, data);
    saveToHistory(envelope);
    callSubscribers(normalizedType, envelope);
    emitLegacyEvent(envelope);

    return envelope;
  }

  function on(type, handler) {
    if (typeof handler !== 'function') {
      return function () {};
    }

    const normalizedType = normalizeType(type);

    if (!normalizedType) {
      return function () {};
    }

    const handlers = getSubscribers(normalizedType);
    handlers.add(handler);

    return function unsubscribe() {
      off(normalizedType, handler);
    };
  }

  function once(type, handler) {
    if (typeof handler !== 'function') {
      return function () {};
    }

    let unsubscribe = function () {};

    const wrappedHandler = function (envelope) {
      unsubscribe();
      handler(envelope);
    };

    unsubscribe = on(type, wrappedHandler);
    return unsubscribe;
  }

  function off(type, handler) {
    const normalizedType = normalizeType(type);
    const handlers = subscribers.get(normalizedType);

    if (!handlers) {
      return false;
    }

    const removed = handlers.delete(handler);

    if (!handlers.size) {
      subscribers.delete(normalizedType);
    }

    return removed;
  }

  function getHistory() {
    return history.map(function (entry) {
      return {
        id: entry.id,
        type: entry.type,
        timestamp: entry.timestamp,
        data: cloneData(entry.data)
      };
    });
  }

  function findByType(type) {
    const normalizedType = normalizeType(type);

    return getHistory().filter(function (entry) {
      return entry.type === normalizedType;
    });
  }

  function getStats() {
    const types = {};

    history.forEach(function (entry) {
      types[entry.type] = (types[entry.type] || 0) + 1;
    });

    let subscriberCount = 0;

    subscribers.forEach(function (handlers) {
      subscriberCount += handlers.size;
    });

    return {
      version: VERSION,
      history_entries: history.length,
      history_limit: MAX_HISTORY,
      subscriber_groups: subscribers.size,
      subscribers: subscriberCount,
      events_by_type: types
    };
  }

  function clearHistory() {
    history.length = 0;
    console.info('[Unified Bus] History cleared.');
    return true;
  }

  window.UnifiedBus = {
    version: VERSION,
    emit: emit,
    on: on,
    once: once,
    off: off,
    getHistory: getHistory,
    findByType: findByType,
    getStats: getStats,
    clearHistory: clearHistory,
    legacyEventName: LEGACY_EVENT_NAME
  };

  if (window.UnifiedTrackingConfig && window.UnifiedTrackingConfig.debug) {
    console.info('[Unified Bus] Event Bus initialized', {
      version: VERSION,
      historyLimit: MAX_HISTORY,
      legacyEvent: LEGACY_EVENT_NAME
    });
  }
})();
